import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from './client'
import { queryKeys } from '../lib/query'
import type { Payment } from '@1130-studio/shared'

interface CreatePaymentData {
  projectId: string
  name: string
  amount: string
  status?: 'pending' | 'paid'
  type?: 'prepayment' | 'milestone' | 'final'
  dueDate?: string | null
}

interface UpdatePaymentData {
  name?: string
  amount?: string
  status?: 'pending' | 'paid'
  type?: 'prepayment' | 'milestone' | 'final'
  dueDate?: string | null
}

// Fetch payments by project
export function usePaymentsByProject(projectId: string) {
  return useQuery({
    queryKey: queryKeys.payments.byProject(projectId),
    queryFn: () => apiClient<Payment[]>(`/payments?projectId=${projectId}`),
    enabled: !!projectId,
  })
}

// Create payment mutation
export function useCreatePayment() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreatePaymentData) =>
      apiClient<Payment>('/payments', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.payments.byProject(data.projectId) })
      queryClient.invalidateQueries({ queryKey: queryKeys.projects.detail(data.projectId) })
    },
  })
}

// Update payment mutation
export function useUpdatePayment() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, projectId, data }: { id: string; projectId: string; data: UpdatePaymentData }) => {
      const result = await apiClient<Payment>(`/payments/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      })
      return { ...result, projectId }
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.payments.byProject(data.projectId) })
      queryClient.invalidateQueries({ queryKey: queryKeys.projects.detail(data.projectId) })
    },
  })
}

// Delete payment mutation
export function useDeletePayment() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, projectId }: { id: string; projectId: string }) => {
      await apiClient(`/payments/${id}`, { method: 'DELETE' })
      return { projectId }
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.payments.byProject(data.projectId) })
      queryClient.invalidateQueries({ queryKey: queryKeys.projects.detail(data.projectId) })
    },
  })
}

// Mark payment as paid
export function useMarkPaymentPaid() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, projectId, paidAt }: { id: string; projectId: string; paidAt?: string }) => {
      const result = await apiClient<Payment>(`/payments/${id}/mark-paid`, {
        method: 'POST',
        body: JSON.stringify({ paidAt }),
      })
      return { ...result, projectId }
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.payments.byProject(data.projectId) })
      queryClient.invalidateQueries({ queryKey: queryKeys.projects.detail(data.projectId) })
    },
  })
}

// Mark payment as pending (undo paid)
export function useMarkPaymentPending() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, projectId }: { id: string; projectId: string }) => {
      const result = await apiClient<Payment>(`/payments/${id}/mark-pending`, {
        method: 'POST',
      })
      return { ...result, projectId }
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.payments.byProject(data.projectId) })
      queryClient.invalidateQueries({ queryKey: queryKeys.projects.detail(data.projectId) })
    },
  })
}
